import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for real-time notifications via WebSocket (mail, calls, etc).
 *
 * @param {Object} options
 * @param {Function} options.onNewEmail - Callback when new email arrives
 * @param {Function} options.onEmailCount - Callback with new email count
 * @param {Function} options.onEmailStatus - Callback with full email status object
 * @param {Function} options.onIncomingCall - Callback when incoming call (call data)
 * @param {Function} options.onCallEnded - Callback when call ends (call_id)
 * @param {Function} options.onConnect - Callback when connected
 * @param {Function} options.onDisconnect - Callback when disconnected
 * @param {boolean} options.enabled - Whether to enable the connection (default: true)
 * @returns {Object} { isConnected, reconnect }
 */
export function useMailWebSocket({
  onNewEmail,
  onEmailCount,
  onEmailStatus,
  onIncomingCall,
  onCallEnded,
  onVisageCall,
  onEmailClassified,
  onJmapStateChange,
  onConnect,
  onDisconnect,
  enabled = true
} = {}) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const enabledRef = useRef(enabled);

  // Store callbacks in refs to avoid dependency issues
  const onNewEmailRef = useRef(onNewEmail);
  const onEmailCountRef = useRef(onEmailCount);
  const onEmailStatusRef = useRef(onEmailStatus);
  const onIncomingCallRef = useRef(onIncomingCall);
  const onCallEndedRef = useRef(onCallEnded);
  const onVisageCallRef = useRef(onVisageCall);
  const onEmailClassifiedRef = useRef(onEmailClassified);
  const onJmapStateChangeRef = useRef(onJmapStateChange);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);

  // Update refs when callbacks change
  useEffect(() => {
    onNewEmailRef.current = onNewEmail;
    onEmailCountRef.current = onEmailCount;
    onEmailStatusRef.current = onEmailStatus;
    onIncomingCallRef.current = onIncomingCall;
    onCallEndedRef.current = onCallEnded;
    onVisageCallRef.current = onVisageCall;
    onEmailClassifiedRef.current = onEmailClassified;
    onJmapStateChangeRef.current = onJmapStateChange;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    enabledRef.current = enabled;
  });

  // Build WebSocket URL. The session token is NOT part of the URL (URLs are logged by every
  // proxy and server between here and the API — credential-in-URL incident #3, 2026-09-07);
  // it is sent as the first frame after open ({type:'auth', token}).
  const getWsUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/mail`;
  }, []);

  // Clean up function
  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Cleanup'); // Normal closure
      wsRef.current = null;
    }
  }, []);

  // Connect function - stable reference
  const connect = useCallback(() => {
    const url = getWsUrl();
    if (!url) {
      console.warn('No token available for WebSocket connection');
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    cleanup();

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        // First frame = auth; the server verifies it before it sends "connected".
        ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('token') }));
        console.log('Mail WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        onConnectRef.current?.();

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          // Handle pong responses
          if (event.data === 'pong') {
            return;
          }

          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'new_email':
              onNewEmailRef.current?.(data.email);
              break;
            case 'email_count':
              onEmailCountRef.current?.(data.count);
              break;
            case 'email_status':
              onEmailStatusRef.current?.(data);
              break;
            case 'incoming_call':
              console.log('Incoming call notification:', data.call);
              onIncomingCallRef.current?.(data.call);
              break;
            case 'call_ended':
              console.log('Call ended notification:', data.call_id);
              onCallEndedRef.current?.(data.call_id, data.call || {});
              break;
            case 'email_classified':
              onEmailClassifiedRef.current?.(data);
              break;
            case 'jmap_state_change':
              onJmapStateChangeRef.current?.(data);
              break;
            case 'visage_incoming_call':
              console.log('Visage incoming call:', data);
              onVisageCallRef.current?.(data);
              break;
            case 'connected':
              console.log('WebSocket:', data.message);
              break;
            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (e) {
          console.warn('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('Mail WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        onDisconnectRef.current?.();

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Only attempt to reconnect if:
        // 1. Not intentionally closed (code 1000)
        // 2. Still enabled
        // 3. Under max attempts
        if (event.code !== 1000 && enabledRef.current && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('Mail WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [getWsUrl, cleanup]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  // Effect to manage connection lifecycle - only runs on mount/unmount and enabled change
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      cleanup();
      setIsConnected(false);
    }

    return cleanup;
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isConnected, reconnect };
}

export default useMailWebSocket;
